// Cart panel.
//
// Renders inside a shared <Modal> (centered overlay, focus-trap +
// Esc-to-close + aria-modal). The earlier Drawer (Miller-column)
// version (2026-04-22) blocked the auth modal underneath when a
// non-registered guest hit Checkout — backdrops at the same z-index
// stacked in DOM order. Reverted to a centered modal on 2026-04-26.
//
// The floating cart toggle stays bespoke because it lives OUTSIDE
// the modal's DOM tree (users can dismiss-and-reopen without the
// trap chasing the toggle).
//
// `closeTray()` runs in the Checkout handler before `onCheckout()`
// so the cart panel hands focus cleanly to whatever opens next
// (auth modal for guests, booking modal for signed-in users).
//
// Plan 84: each Parent/Child Session line gets an inline `[1] [2] [3] [4]`
// quantity selector that re-computes the line total via the shared tier
// helper. Non Parent/Child lines render the catalog price unchanged.

import { motion } from 'motion/react';
import { ArrowRight, ShoppingCart, X } from '../../shared/icons';
import { cn } from '../../shared/utils/cn';
import { Button, Modal } from '../../shared/ui';
import { useCart, type CartItem } from '../booking/CartProvider';
import { isParentChildBase } from '../../shared/services/serviceTypes';
import { parentChildPrice } from '../../shared/services/parentChildPricing';
import { formatPriceUSD } from '../../shared/format/money';

export interface CartTrayProps {
  onCheckout: () => void;
}

/** Display-side line total — mirrors the CartProvider's internal helper. */
function lineTotalCents(item: CartItem): number {
  if (isParentChildBase(item.service)) {
    return parentChildPrice(item.childCount ?? 1);
  }
  return item.service.price;
}

export const CartTray = ({ onCheckout }: CartTrayProps) => {
  const { cart, remove, setChildCount, total, isOpen, closeTray, toggleTray } = useCart();
  const modalOpen = isOpen && cart.length > 0;

  const handleCheckout = () => {
    closeTray();
    onCheckout();
  };

  return (
    <>
      <Modal isOpen={modalOpen} onClose={closeTray} title={`Your Cart (${cart.length})`} size="sm">
        <div className="space-y-6">
          <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-hide">
            {cart.map((item) => {
              const { service } = item;
              const isParentChild = isParentChildBase(service);
              const childCount = item.childCount ?? 1;
              const linePrice = lineTotalCents(item);
              return (
                <div key={service.id} className="flex justify-between items-start group gap-3">
                  <div className="flex-grow min-w-0">
                    <p className="font-bold text-sm text-stone-900">
                      {service.name}
                      {isParentChild && (
                        <span className="ml-2 text-xs font-medium text-stone-500">
                          × {childCount} {childCount === 1 ? 'child' : 'children'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-stone-400 capitalize">{service.category}</p>
                    {isParentChild && (
                      <div
                        className="flex gap-1 mt-2"
                        data-testid={`cart-child-count-${service.id}`}
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setChildCount(service.id, n)}
                            aria-label={`Set ${n} ${n === 1 ? 'child' : 'children'}`}
                            aria-pressed={childCount === n}
                            className={cn(
                              'px-2 py-1 rounded-md text-xs font-bold border transition-colors',
                              childCount === n
                                ? 'bg-stone-900 border-stone-900 text-stone-50'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
                            )}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-medium text-sm text-stone-600">
                      {formatPriceUSD(linePrice)}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(service.id)}
                      className="p-1.5 bg-stone-100 hover:bg-red-50 hover:text-red-500 rounded-lg transition-all"
                      aria-label={`Remove ${service.name}`}
                    >
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-6 border-t border-stone-100 space-y-6">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-stone-400 uppercase tracking-widest">
                Total
              </span>
              <span className="text-2xl font-serif">{formatPriceUSD(total)}</span>
            </div>
            <Button fullWidth size="lg" onClick={handleCheckout}>
              <span>Checkout</span>
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </Modal>

      <div className="fixed bottom-8 right-8 z-[90] pointer-events-none">
        <motion.button
          type="button"
          onClick={toggleTray}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            'w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all pointer-events-auto relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2',
            cart.length > 0
              ? 'bg-stone-900 text-stone-50'
              : 'bg-white text-stone-900 border border-stone-200'
          )}
          aria-label={cart.length > 0 ? `Open cart (${cart.length} items)` : 'Open cart'}
        >
          <ShoppingCart className="w-6 h-6" aria-hidden="true" />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center border-4 border-stone-50">
              {cart.length}
            </span>
          )}
        </motion.button>
      </div>
    </>
  );
};
